import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { get, post, patch } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader } from "@/components/common/PageHeader";
import { Skeleton, EmptyState } from "@/components/common/EmptyState";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { dateFmt } from "@/lib/format";
import { ChevronLeft, ChevronRight, Clock, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

function mondayOf(d) {
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  const m = new Date(d);
  m.setDate(d.getDate() + diff);
  m.setHours(0, 0, 0, 0);
  return m;
}

export default function MyTimesheet() {
  const { me } = useApp();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [cursor, setCursor] = useState(() => mondayOf(new Date()));
  const { data: projects = [] } = useQuery({ queryKey: ["projects"], queryFn: () => get("/pm/projects") });
  const pmap = useMemo(() => Object.fromEntries(projects.map((p) => [p.id, p.name])), [projects]);
  const myProjects = useMemo(() => projects.filter((p) => (p.members || []).some((m) => m.user_id === me?.id)), [projects, me]);
  const { data: itemsByProject = {} } = useQuery({
    queryKey: ["ts-my-items", myProjects.map((p) => p.id).join(",")],
    queryFn: async () => {
      const entries = await Promise.all(myProjects.map(async (p) => [p.id, await get(`/pm/projects/${p.id}/items`)]));
      return Object.fromEntries(entries);
    },
    enabled: myProjects.length > 0,
  });
  const tmap = useMemo(() => {
    const map = {};
    Object.values(itemsByProject).forEach((items) => items.forEach((i) => { map[i.id] = i.title; }));
    return map;
  }, [itemsByProject]);

  const { data: weeks, isLoading } = useQuery({ queryKey: ["ts-weeks", me?.id], queryFn: () => get(`/timesheets/weeks?employee_id=${me?.id}`), enabled: !!me });
  const week = (weeks || []).find((w) => new Date(w.week_start).toDateString() === cursor.toDateString());

  const ensureWeekMutation = useMutation({
    mutationFn: () => post("/timesheets/weeks", { employee_id: me?.id, week_start: cursor.toISOString() }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ts-weeks", me?.id] }),
  });
  const updateWeek = useMutation({
    mutationFn: ({ wid, body }) => patch(`/timesheets/weeks/${wid}`, body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["ts-weeks", me?.id] }); },
  });

  const removeEntry = (idx) => {
    const entries = week.entries.filter((_, i) => i !== idx);
    updateWeek.mutate({ wid: week.id, body: { entries } });
  };
  const submit = () => updateWeek.mutate({ wid: week.id, body: { status: "Pending" } }, { onSuccess: () => toast.success("Submitted for approval") });

  if (isLoading) return <Skeleton className="h-64" />;

  return (
    <div>
      <PageHeader title="My Timesheet" subtitle={`Week of ${cursor.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`}>
        <div className="flex items-center gap-1 rounded-lg border border-border bg-card px-1">
          <button data-testid="ts-prev" className="p-1.5 text-muted-foreground hover:text-foreground" onClick={() => setCursor((c) => { const n = new Date(c); n.setDate(n.getDate() - 7); return n; })}><ChevronLeft className="h-4 w-4" /></button>
          <span className="px-2 text-sm">{cursor.toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>
          <button data-testid="ts-next" className="p-1.5 text-muted-foreground hover:text-foreground" onClick={() => setCursor((c) => { const n = new Date(c); n.setDate(n.getDate() + 7); return n; })}><ChevronRight className="h-4 w-4" /></button>
        </div>
        {week && week.status === "Draft" && (
          <Button size="sm" variant="outline" data-testid="add-entry-btn" onClick={() => navigate("/timesheets/add-entry", { state: { weekStart: cursor.toISOString() } })}><Plus className="mr-1 h-3.5 w-3.5" />Add Entry</Button>
        )}
        <Button data-testid="submit-timesheet" disabled={!week || week.status !== "Draft" || !week.entries?.length} onClick={submit}>Submit for Approval</Button>
      </PageHeader>

      {!week ? (
        <EmptyState icon={Clock} title="No timesheet this week" subtitle="Start one to begin logging time."
          action={<Button data-testid="start-week-btn" onClick={() => ensureWeekMutation.mutate()}>Start This Week's Timesheet</Button>} />
      ) : (
        <div className="overflow-hidden rounded-xl border border-border bg-card">
          <table className="w-full text-sm" data-testid="timesheet-table">
            <thead><tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
              <th className="px-4 py-3 font-semibold">Date</th>
              <th className="px-4 py-3 font-semibold">Summary</th>
              <th className="px-4 py-3 font-semibold">Project</th>
              <th className="px-4 py-3 font-semibold">Task</th>
              <th className="px-3 py-3 text-right font-semibold">Hours</th>
              <th className="w-8"></th></tr></thead>
            <tbody>
              {(week.entries || []).map((e, i) => (
                <tr key={i} className="group border-b border-border last:border-0">
                  <td className="px-4 py-3 text-muted-foreground">{dateFmt(e.date)}</td>
                  <td className="px-4 py-3">{e.summary}</td>
                  <td className="px-4 py-3 text-muted-foreground">{pmap[e.project_id] || "—"}</td>
                  <td className="px-4 py-3 text-muted-foreground">{tmap[e.work_item_id] || "—"}</td>
                  <td className="px-3 py-3 text-right font-mono">{e.hours}h</td>
                  <td className="px-2">
                    {week.status === "Draft" && (
                      <button className="hidden text-muted-foreground hover:text-red-600 group-hover:block" onClick={() => removeEntry(i)}><Trash2 className="h-3.5 w-3.5" /></button>
                    )}
                  </td>
                </tr>
              ))}
              {(!week.entries || week.entries.length === 0) && (
                <tr><td colSpan={6} className="px-4 py-6 text-center text-sm text-muted-foreground">No entries yet.</td></tr>
              )}
              <tr className="bg-muted/40 font-semibold">
                <td colSpan={4} className="px-4 py-3">Total</td>
                <td className="px-3 py-3 text-right font-mono">{(week.entries || []).reduce((a, e) => a + e.hours, 0)}h</td>
                <td></td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
      {week && <div className="mt-3"><StatusBadge status={week.status} /></div>}
    </div>
  );
}
